import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import SelectMenu from "../../Shared/SelectMenu";
import RadioGroup from "../../Shared/RadioGroup";
import FormFooter from "../../Shared/FormFooter";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import AlertRed from "../../Shared/AlertRed";
import {
  getAccountSettings,
  updateAccountSettings,
} from "../../../api/settingsApi";
import { CURRENCY_CODE_LIST } from "../../../config/constants";

const DisplayPreferencesFormContent = ({ userData }) => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      currency: userData.currency || "CAD",
      timeFormat: userData.timeFormat || "12h",
    },
  });

  const selectedCurrency = watch("currency");

  // Update mutation
  const { mutate: updateSettings, isPending: isUpdating } = useMutation({
    mutationFn: updateAccountSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accountSettings"], exact: true });
      setError(null);
      setSuccess(true);
    },
    onError: (err) => {
      setError({
        title: "Error",
        message:
          err.response?.data?.error?.message ||
          "Failed to update display preferences",
      });
    },
  });

  const onSubmit = async (data) => {
    // Don't save if nothing has changed
    if (!isDirty) {
      return;
    }

    setError(null);
    setSuccess(false);
    updateSettings(data);
  };

  const currencyOptions = CURRENCY_CODE_LIST.map((currency) => ({
    id: currency.id,
    name: `${currency.id} (${currency.symbol})`,
  }));

  const timeFormatOptions = [
    { id: "12h", title: "12-hour format (AM/PM)" },
    { id: "24h", title: "24-hour format" },
  ];

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <SelectMenu
            label="Currency"
            data={currencyOptions}
            defaultSelected={
              currencyOptions.find((c) => c.id === selectedCurrency) ||
              currencyOptions[0]
            }
            onSelect={(currency) => {
              setValue("currency", currency.id, { shouldDirty: true });
            }}
          />
        </div>

        <div className="col-span-full">
          <RadioGroup
            label="Time Format"
            name="timeFormat"
            options={timeFormatOptions}
            register={register}
            required="Time format is required"
            layout="horizontal"
          />
          {errors.timeFormat && (
            <p className="mt-2 text-sm text-signal-red-600">
              {errors.timeFormat.message}
            </p>
          )}
        </div>
      </div>

      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {error ? (
        <div className="mt-4">
          <AlertRed title={error.title} message={error.message} />
        </div>
      ) : null}

      {success && (
        <div className="mt-4">
          <div className="rounded-md bg-signal-green-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-accent-700">
                  Display preferences updated successfully
                </h3>
              </div>
            </div>
          </div>
        </div>
      )}
    </TabFormWrapper>
  );
};

const DisplayPreferencesForm = () => {
  // Fetch user data
  const { data: userData, isLoading } = useQuery({
    queryKey: ["accountSettings"],
    queryFn: getAccountSettings,
  });

  if (isLoading) {
    return (
      <TabFormWrapper>
        <div className="flex justify-center items-center py-8">
          <div className="text-ink-500">Loading...</div>
        </div>
      </TabFormWrapper>
    );
  }

  if (!userData?.user) {
    return (
      <TabFormWrapper>
        <div className="flex justify-center items-center py-8">
          <div className="text-ink-500">No user data available</div>
        </div>
      </TabFormWrapper>
    );
  }

  return <DisplayPreferencesFormContent userData={userData.user} />;
};

export default DisplayPreferencesForm;
